import { cn } from "@/lib/utils";
import * as shared from "@/styles/shared";
import type { IconComponent } from "@/types/ui";
import type { InputHTMLAttributes } from "react";
import { forwardRef, useState } from "react";
import * as styles from "./Input.styles";

const inputVariants = {
  default: "border border-input bg-transparent shadow-sm",
  outline: "border-2 border-input bg-transparent",
  ghost: "border-b-2 border-input bg-transparent rounded-none shadow-none",
};

interface InputProps extends InputHTMLAttributes<HTMLInputElement> {
  label?: React.ReactNode;
  icon?: IconComponent;
  variant?: keyof typeof inputVariants;
  floatingLabel?: boolean;
}

const Input = forwardRef<HTMLInputElement, InputProps>(
  ({ className, label, id, icon: Icon, variant = "default", floatingLabel = false, type, ...props }, ref) => {
    const [isFocused, setIsFocused] = useState(false);
    const [hasValue, setHasValue] = useState(false);
    const inputId = id || props.name || props.placeholder || `input-${Date.now()}`;

    const handleFocus = (e: React.FocusEvent<HTMLInputElement>) => {
      setIsFocused(true);
      props.onFocus?.(e);
    };

    const handleBlur = (e: React.FocusEvent<HTMLInputElement>) => {
      setIsFocused(false);
      setHasValue(!!e.target.value);
      props.onBlur?.(e);
    };

    const handleChange = (e: React.ChangeEvent<HTMLInputElement>) => {
      setHasValue(!!e.target.value);
      props.onChange?.(e);
    };

    const baseClass = inputVariants[variant] ?? inputVariants.default;
    const inputClass = cn(
      "flex h-9 w-full rounded-md text-sm transition-all duration-200 placeholder:text-muted-foreground disabled:cursor-not-allowed disabled:opacity-50",
      "focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2",
      "disabled:cursor-not-allowed disabled:opacity-50",
      baseClass,
      variant === "ghost" && "hover:border-primary/50",
      className,
    );

    if (floatingLabel && label) {
      const floatingLabelClass = cn(
        "absolute left-3 top-1/2 -translate-y-1/2 text-sm text-muted-foreground pointer-events-none transition-all duration-200 origin-left",
        (isFocused || hasValue) && "-translate-y-[110%] text-xs text-primary",
      );

      return (
        <div className={styles.floatingLabelContainer}>
          <input
            ref={ref}
            id={inputId}
            type={type}
            className={cn(inputClass, styles.inputWithIconFloating, Icon && "pl-9", variant === "ghost" && "pl-9 pt-4 pb-2")}
            onFocus={handleFocus}
            onBlur={handleBlur}
            onChange={handleChange}
            {...props}
          />
          {Icon && <Icon className={shared.inputIcon} />}
          <label htmlFor={inputId} className={floatingLabelClass}>
            {label}
          </label>
        </div>
      );
    }

    return (
      <div className={styles.fullWidth}>
        {label && (
          <label htmlFor={inputId} className={styles.label}>
            {label}
          </label>
        )}
        <div className={styles.relative}>
          {Icon && <Icon className={shared.inputIcon} />}
          <input
            ref={ref}
            id={inputId}
            type={type}
            className={cn(inputClass, Icon && "pl-9", className)}
            onFocus={handleFocus}
            onBlur={handleBlur}
            onChange={handleChange}
            {...props}
          />
        </div>
      </div>
    );
  },
);
Input.displayName = "Input";

export { Input };
export type { InputProps };
